import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAppColors } from '../../hooks/useAppColors';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatters';
import { getSortedPlans, calculatePlanTermPrice, calculateOfferPrice, getDiscountText, getOfferForPlanTerm, isTargetedOfferForPlan } from './billingUtils';
import type { MembershipPlan, MemberPlanOffer } from '../../hooks/usePayments';

interface PlanSelectorProps {
  plans: MembershipPlan[] | undefined;
  dob: string | null;
  selectedPlanId: string | null;
  onSelectPlan: (planId: string) => void;
  /** Show compact row style (for renewal/inline) vs card grid */
  variant?: 'card' | 'row';
  /** Include "Skip for Now" option in the grid */
  showSkipOption?: boolean;
  /** Plan ID for the skip option */
  skipPlanId?: string;
  /** Selected term in months for pricing calculation */
  selectedTermMonths?: number;
  /** Show discount badge on plans */
  showDiscountBadge?: boolean;
  /** Plan IDs that are allowed even if inactive (targeted discount recipients) */
  allowedInactivePlanIds?: string[];
  /** Member-specific offers from catalog (used for tier/targeted discount pricing) */
  offers?: MemberPlanOffer[];
}

export default function PlanSelector({
  plans,
  dob,
  selectedPlanId,
  onSelectPlan,
  variant = 'row',
  showSkipOption = false,
  skipPlanId = 'skip',
  selectedTermMonths,
  showDiscountBadge = false,
  allowedInactivePlanIds = [],
  offers,
}: PlanSelectorProps) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const allowedInactiveSet = new Set(allowedInactivePlanIds);
  const visiblePlans = plans?.filter(p =>
    (p.is_active && !p.is_archived) || allowedInactiveSet.has(p.id)
  ) || [];
  const sorted = getSortedPlans(visiblePlans, dob);
  const termMonths = selectedTermMonths || 1;

  if (variant === 'card') {
    return (
      <View style={styles.cardGrid}>
        {sorted.map(({ plan, slug }) => {
          const isSelected = selectedPlanId === plan.id;
          const offer = offers ? getOfferForPlanTerm(offers, plan.id, termMonths) : null;
          const pricing = offer ? calculateOfferPrice(plan, termMonths, offer) : calculatePlanTermPrice(plan, termMonths);
          const hasSavings = pricing.discountAmount > 0;
          const isTargeted = isTargetedOfferForPlan(offers, plan.id);
          const planBadgeText = t(`plans.${slug}.badge`, { defaultValue: '' });

          return (
            <TouchableOpacity
              key={plan.id}
              style={[
                styles.planCard,
                {
                  backgroundColor: isSelected ? colors.accent + '18' : colors.card,
                  borderColor: isSelected ? colors.accent : colors.border,
                },
              ]}
              onPress={() => onSelectPlan(plan.id)}
              activeOpacity={0.7}
            >
              <View style={styles.planHeader}>
                <Text style={[styles.planIndex, { color: colors.textTertiary }]}>
                  {String(plan.sort_order).padStart(2, '0')}
                </Text>
                <View style={styles.planHeaderBadges}>
                  {isTargeted && (
                    <View style={[styles.targetedBadge, { backgroundColor: colors.warning + '22' }]}>
                      <Icon name="star-shooting" size={10} color={colors.warning} />
                      <Text style={[styles.targetedBadgeText, { color: colors.warning }]}>
                        {t('billing.targetedOfferBadge')}
                      </Text>
                    </View>
                  )}
                  {planBadgeText && (
                    <View style={[styles.badge, { backgroundColor: colors.accent + '20' }]}>
                      <Text style={[styles.badgeText, { color: colors.accent }]}>
                        {planBadgeText}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
              <Text style={[styles.planName, { color: colors.text }]}>
                {t(`plans.${plan.machine_name}.name`, { defaultValue: plan.machine_name })}
              </Text>
              <View style={styles.planPriceRow}>
                {hasSavings && (
                  <Text style={[styles.planPriceStrike, { color: colors.textTertiary }]}>
                    {formatCurrency(pricing.subtotal / termMonths)}
                  </Text>
                )}
                <View style={styles.planPriceMain}>
                  <Text style={[styles.planPrice, { color: colors.text }]}>
                    {formatCurrency(pricing.finalAmount / termMonths)}
                  </Text>
                  <Text style={[styles.planPeriod, { color: colors.textSecondary }]}>
                    /{t('billing.perMonth')}
                  </Text>
                </View>
              </View>
              {showDiscountBadge && hasSavings && pricing.discount && (
                <View style={[styles.discountBadge, { backgroundColor: colors.accent + '20' }]}>
                  <Text style={[styles.discountBadgeText, { color: colors.accent }]}>
                    {getDiscountText(pricing.discount, pricing.subtotal, pricing.finalAmount, formatCurrency, t, termMonths)}
                  </Text>
                </View>
              )}
              <Text style={[styles.planDesc, { color: colors.textSecondary }]}>
                {t(`plans.${plan.machine_name}.description`, { defaultValue: '' })}
              </Text>
              {t(`plans.${slug}.note`, { defaultValue: '' }) && (
                <Text style={[styles.planNote, { color: colors.textTertiary }]}>
                  {t(`plans.${slug}.note`)}
                </Text>
              )}
              {isSelected && (
                <View style={styles.selectedIndicator}>
                  <Icon name="check-circle" size={20} color={colors.accent} />
                </View>
              )}
            </TouchableOpacity>
          );
        })}
        {showSkipOption && (
          <TouchableOpacity
            key={skipPlanId}
            style={[
              styles.planCard,
              {
                backgroundColor: selectedPlanId === skipPlanId ? colors.accent + '18' : colors.card,
                borderColor: selectedPlanId === skipPlanId ? colors.accent : colors.border,
              },
            ]}
            onPress={() => onSelectPlan(skipPlanId)}
            activeOpacity={0.7}
          >
            <View style={styles.planHeader}>
              <Text style={[styles.planIndex, { color: colors.textTertiary }]}>—</Text>
            </View>
            <Text style={[styles.planName, { color: colors.text }]}>{t('subscription.skipPlanTitle')}</Text>
            <Text style={[styles.planDesc, { color: colors.textSecondary }]}>
              {t('subscription.skipPlanDesc')}
            </Text>
            {selectedPlanId === skipPlanId && (
              <View style={styles.selectedIndicator}>
                <Icon name="check-circle" size={20} color={colors.accent} />
              </View>
            )}
          </TouchableOpacity>
        )}
      </View>
    );
  }

  // Row variant (compact, for renewal/inline)
  return (
    <View style={styles.rowList}>
      {sorted.map(({ plan }) => {
        const isSelected = selectedPlanId === plan.id;
        const offer = offers ? getOfferForPlanTerm(offers, plan.id, termMonths) : null;
        const pricing = offer ? calculateOfferPrice(plan, termMonths, offer) : calculatePlanTermPrice(plan, termMonths);
        const hasSavings = showDiscountBadge && pricing.discountAmount > 0;
        const isTargeted = isTargetedOfferForPlan(offers, plan.id);

        return (
          <TouchableOpacity
            key={plan.id}
            style={[
              styles.planRow,
              {
                backgroundColor: isSelected ? colors.accent + '18' : colors.card,
                borderColor: isSelected ? colors.accent : colors.border,
              },
            ]}
            onPress={() => onSelectPlan(plan.id)}
            activeOpacity={0.7}
          >
            <View style={{ flex: 1 }}>
              <View style={styles.rowNameRow}>
                <Text style={[styles.rowPlanName, { color: colors.text }]}>
                  {t(`plans.${plan.machine_name}.name`, { defaultValue: plan.machine_name })}
                </Text>
                {isTargeted && (
                  <View style={[styles.targetedBadge, { backgroundColor: colors.warning + '22' }]}>
                    <Icon name="star-shooting" size={10} color={colors.warning} />
                    <Text style={[styles.targetedBadgeText, { color: colors.warning }]}>
                      {t('billing.targetedOfferBadge')}
                    </Text>
                  </View>
                )}
              </View>
              <View style={styles.rowPriceRow}>
                {hasSavings && (
                  <Text style={[styles.rowPriceStrike, { color: colors.textTertiary }]}>
                    {formatCurrency(pricing.subtotal / termMonths)}
                  </Text>
                )}
                <View style={styles.rowPriceMain}>
                  <Text style={[styles.rowPlanPrice, { color: colors.textSecondary }]}>
                    {formatCurrency(pricing.finalAmount / termMonths)}
                  </Text>
                  <Text style={[styles.rowPlanPeriod, { color: colors.textTertiary }]}>
                    /{t('billing.perMonth')}
                  </Text>
                </View>
              </View>
              {hasSavings && pricing.discount && (
                <Text style={[styles.rowDiscount, { color: colors.accent }]}>
                  {getDiscountText(pricing.discount, pricing.subtotal, pricing.finalAmount, formatCurrency, t, termMonths)}
                </Text>
              )}
            </View>
            {isSelected ? (
              <Icon name="check-circle" size={20} color={colors.accent} />
            ) : (
              <Icon name="chevron-right" size={18} color={colors.textTertiary} />
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  // Card grid variant
  cardGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  planCard: {
    width: '48%',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    position: 'relative',
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  planHeaderBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
    flexShrink: 1,
    justifyContent: 'flex-end',
  },
  planIndex: {
    fontSize: 12,
    fontWeight: '600',
    opacity: 0.6,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
  targetedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
  },
  targetedBadgeText: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  planName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  planPriceRow: {
    marginBottom: 6,
  },
  planPriceStrike: {
    fontSize: 11,
    textDecorationLine: 'line-through',
  },
  planPriceMain: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 2,
  },
  planPrice: {
    fontSize: 18,
    fontWeight: '700',
  },
  planPeriod: {
    fontSize: 12,
  },
  discountBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    marginTop: 4,
  },
  discountBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  planDesc: {
    fontSize: 12,
    lineHeight: 16,
  },
  planNote: {
    fontSize: 10,
    fontWeight: '600',
    marginTop: 4,
    opacity: 0.7,
    textTransform: 'uppercase',
  },
  selectedIndicator: {
    position: 'absolute',
    top: 10,
    right: 10,
  },

  // Row variant
  rowList: { gap: 8 },
  planRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  rowPlanName: { fontSize: 14, fontWeight: '500' },
  rowNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  rowPriceRow: {
    marginTop: 2,
  },
  rowPriceStrike: {
    fontSize: 10,
    textDecorationLine: 'line-through',
  },
  rowPriceMain: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 2,
  },
  rowPlanPrice: { fontSize: 12 },
  rowPlanPeriod: {
    fontSize: 11,
  },
  rowDiscount: {
    fontSize: 11,
    marginTop: 2,
    fontWeight: '500',
  },
});
